import Accordion, { AccordionMode, AutoExpandItems } from "@common/controls/Accordion";
import Icon from "@common/controls/Icon/component";
import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import classNames from "classnames";
import * as React from "react";
import { type Courses } from "../types";
import ModuleContent from "./Module/Content";
import ModuleHeading from "./Module/Heading";

export type Props = {
  activeCourse: Courses.CourseAppState | null;
  activeItemIndex?: number;
  completeIconType?: IconType;
  navigationType: "accordion" | "nested";
  onGoBack: () => void;
  onLessonClick: (id: string) => void;
  onModuleSelected: (index: number) => void;
  module: Courses.CourseModule | null;
  modules: Courses.CourseModule[];
  style?: React.CSSProperties;
};

export default (props: Props) => {
  const i18n = useI18n();

  const onGoBack = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      props.onGoBack();
    },
    [props.onGoBack]
  );

  if (!props.modules.length) {
    return <div className="no_lessons">{i18n.infoNoLessons}</div>;
  }

  return (
    <Accordion
      activeItemIndex={props.activeItemIndex}
      autoExpand={props.navigationType === "nested" && !props.module ? AutoExpandItems.None : AutoExpandItems.First}
      className={classNames("course_modules", props.navigationType)}
      noAnimation={props.navigationType === "nested"}
      mode={props.navigationType === "nested" || !props.module ? AccordionMode.Toggle : AccordionMode.Static}
      onItemClick={props.navigationType === "nested" && props.module ? undefined : props.onModuleSelected}
      onItemActivated={props.onModuleSelected}
      style={props.style}
    >
      {props.module ? (
        <Accordion.Item
          headingTag="div"
          label={
            <ModuleHeading
              prepend={<Icon className="back clickable" icon={IconType.ArrowLeft} onClick={onGoBack} />}
              name={props.module.name}
              total={props.activeCourse!.lessons.filter((x) => x.moduleId === props.module!.id).length}
              completed={
                props.activeCourse!.lessons.filter((x) => x.moduleId === props.module!.id && x.isComplete).length
              }
            />
          }
        >
          <ModuleContent
            activeLessonId={props.activeCourse!.activeLessonId}
            items={props.activeCourse!.lessons.filter((x) => x.moduleId === props.module!.id)}
            onLessonClick={props.onLessonClick}
            completeIconType={props.completeIconType}
          />
        </Accordion.Item>
      ) : (
        props.modules.map((s) => (
          <Accordion.Item
            key={s.id}
            headingTag="div"
            label={
              <ModuleHeading
                name={s.name}
                total={props.activeCourse!.lessons.filter((x) => x.moduleId === s.id).length}
                completed={props.activeCourse!.lessons.filter((x) => x.moduleId === s.id && x.isComplete).length}
              />
            }
          >
            {props.navigationType === "accordion" ? (
              <ModuleContent
                activeLessonId={props.activeCourse!.activeLessonId}
                items={props.activeCourse!.lessons.filter((x) => x.moduleId === s.id)}
                onLessonClick={props.onLessonClick}
                completeIconType={props.completeIconType}
              />
            ) : null}
          </Accordion.Item>
        ))
      )}
    </Accordion>
  );
};
